import React, { useState } from 'react';
import { OptImg } from '../../ui/OptImg';
import { GalleryBrowser } from '../../../pages/GalleryPage';
import { motion } from 'framer-motion';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';
import useEmblaCarousel from 'embla-carousel-react';
import { SectionShell, SectionHeader, isDarkBg, inPreviewFrame } from '../shared';

const toImages = (section) => {
  const p = section.props || {};
  const manual = (p.images || []).filter((i) => i.image?.url).map((i) => ({ url: i.image.url, alt: i.image.alt_text || i.caption || '', caption: i.caption }));
  const dynamic = (section.data || []).map((d) => ({ url: d.image || d.url, alt: d.title || d.alt_text || '', caption: d.title, description: d.description, meta: d.category || d.location })).filter((i) => i.url);
  return [...manual, ...dynamic];
};

const Lightbox = ({ images, index, onClose, setIndex }) => (
  <div className="fixed inset-0 z-[100] bg-black/95 flex items-center justify-center" onClick={onClose} data-testid="gallery-lightbox">
    <button className="absolute top-6 right-6 text-white/80 hover:text-white" onClick={onClose} aria-label="Close"><X className="w-7 h-7" /></button>
    <button className="absolute left-4 text-white/80 hover:text-white p-2" onClick={(e) => { e.stopPropagation(); setIndex((index - 1 + images.length) % images.length); }} aria-label="Previous"><ChevronLeft className="w-8 h-8" /></button>
    <OptImg sizes="(min-width: 1024px) 33vw, (min-width: 640px) 50vw, 100vw" src={images[index].url} alt={images[index].alt} className="max-h-[85vh] max-w-[90vw] object-contain" onClick={(e) => e.stopPropagation()} />
    <button className="absolute right-4 text-white/80 hover:text-white p-2" onClick={(e) => { e.stopPropagation(); setIndex((index + 1) % images.length); }} aria-label="Next"><ChevronRight className="w-8 h-8" /></button>
    {images[index].caption && <p className="absolute bottom-8 text-white/80 text-sm">{images[index].caption}</p>}
  </div>
);

export const GallerySection = ({ section }) => section.layout === 'interactive'
  ? <SectionShell section={{ ...section, style: { ...(section.style || {}), padding: 'none' } }} container="none" testId="section-gallery"><div className="cms-native"><GalleryBrowser embedded stickyFilters={!inPreviewFrame()} /></div></SectionShell>
  : <GalleryStatic section={section} />;

const GalleryStatic = ({ section }) => {
  const images = toImages(section);
  const [open, setOpen] = useState(null);
  const [emblaRef, embla] = useEmblaCarousel({ loop: true, align: 'start' });
  const layout = section.layout || 'grid';
  const dark = isDarkBg(section.style);

  return (
    <SectionShell section={section}>
      <SectionHeader props={section.props} style={section.style} dark={dark} />
      {images.length === 0 && <p className="text-sm text-neutral-400 italic">No images selected yet.</p>}
      {layout === 'carousel' ? (
        <div className="relative">
          <div className="overflow-hidden" ref={emblaRef}>
            <div className="flex gap-4">
              {images.map((img, i) => (
                <button key={i} className="flex-[0_0_85%] md:flex-[0_0_45%] lg:flex-[0_0_32%] group text-left" onClick={() => setOpen(i)}>
                  <OptImg sizes="(min-width: 1024px) 33vw, (min-width: 640px) 50vw, 100vw" src={img.url} alt={img.alt} className="w-full aspect-[4/3] object-cover rounded-[var(--site-radius)] transition-transform group-hover:scale-[1.02]" loading="lazy" />
                  {img.caption && <p className={`mt-3 text-sm ${dark ? 'text-neutral-300' : 'text-neutral-600'}`}>{img.caption}</p>}
                </button>
              ))}
            </div>
          </div>
          <button onClick={() => embla?.scrollPrev()} className="absolute -left-3 top-1/3 bg-white shadow p-2 rounded-full hover:bg-neutral-100" aria-label="Previous"><ChevronLeft className="w-5 h-5 text-neutral-900" /></button>
          <button onClick={() => embla?.scrollNext()} className="absolute -right-3 top-1/3 bg-white shadow p-2 rounded-full hover:bg-neutral-100" aria-label="Next"><ChevronRight className="w-5 h-5 text-neutral-900" /></button>
        </div>
      ) : layout === 'masonry' ? (
        <div className="columns-2 md:columns-3 gap-4 [&>*]:mb-4">
          {images.map((img, i) => (
            <motion.button key={i} initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} className="block w-full break-inside-avoid group" onClick={() => setOpen(i)}>
              <OptImg sizes="(min-width: 1024px) 33vw, (min-width: 640px) 50vw, 100vw" src={img.url} alt={img.alt} className="w-full rounded-[var(--site-radius)] transition-transform group-hover:scale-[1.01]" loading="lazy" />
            </motion.button>
          ))}
        </div>
      ) : (
        // Mirrors the coded HomePage gallery preview: square tiles with hover overlay.
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          {images.map((img, i) => (
            <motion.button key={i} initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }} className="relative aspect-square overflow-hidden group cursor-pointer text-left rounded-[var(--site-radius)]" onClick={() => setOpen(i)}>
              <OptImg sizes="(min-width: 1024px) 33vw, (min-width: 640px) 50vw, 100vw" src={img.url} alt={img.alt} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" loading="lazy" />
              <div className="absolute inset-0 bg-neutral-900/0 group-hover:bg-neutral-900/60 transition-colors flex items-end p-4">
                <div className="translate-y-4 opacity-0 group-hover:translate-y-0 group-hover:opacity-100 transition-all">
                  {img.caption && <p className="text-white font-medium">{img.caption}</p>}
                  {img.description && <p className="text-neutral-300 text-sm">{img.description}</p>}
                </div>
              </div>
            </motion.button>
          ))}
        </div>
      )}
      {open !== null && images[open] && <Lightbox images={images} index={open} onClose={() => setOpen(null)} setIndex={setOpen} />}
    </SectionShell>
  );
};

export const BeforeAfterSection = ({ section }) => {
  const p = section.props || {};
  const [pos, setPos] = useState(50);
  const dark = isDarkBg(section.style);
  const before = p.before_image?.url, after = p.after_image?.url;
  return (
    <SectionShell section={section} container="lg">
      <SectionHeader props={{ title: p.title, body: p.body }} style={{ align: 'center' }} dark={dark} />
      {section.layout === 'side_by_side' ? (
        <div className="grid md:grid-cols-2 gap-6">
          {[['Before', before, p.before_image?.alt_text], ['After', after, p.after_image?.alt_text]].map(([label, url, alt]) => (
            <figure key={label}>{url && <OptImg sizes="(min-width: 1024px) 33vw, (min-width: 640px) 50vw, 100vw" src={url} alt={alt || label} className="w-full aspect-[4/3] object-cover rounded-[var(--site-radius)]" loading="lazy" />}<figcaption className={`mt-3 label-overline ${dark ? 'text-neutral-300' : ''}`}>{label}</figcaption></figure>
          ))}
        </div>
      ) : (
        <div className="relative aspect-[16/10] overflow-hidden select-none rounded-[var(--site-radius)]" data-testid="before-after-slider">
          {after && <OptImg sizes="(min-width: 1024px) 33vw, (min-width: 640px) 50vw, 100vw" src={after} alt={p.after_image?.alt_text || 'After'} className="absolute inset-0 w-full h-full object-cover" />}
          {before && <OptImg sizes="(min-width: 1024px) 33vw, (min-width: 640px) 50vw, 100vw" src={before} alt={p.before_image?.alt_text || 'Before'} className="absolute inset-0 w-full h-full object-cover" style={{ clipPath: `inset(0 ${100 - pos}% 0 0)` }} />}
          <div className="absolute inset-y-0 w-0.5 bg-white shadow" style={{ left: `${pos}%` }} />
          <input type="range" min={0} max={100} value={pos} onChange={(e) => setPos(Number(e.target.value))} className="absolute inset-0 w-full h-full opacity-0 cursor-ew-resize" aria-label="Compare before and after" />
          <span className="absolute top-4 left-4 bg-black/60 text-white text-xs px-2 py-1 uppercase tracking-wider">Before</span>
          <span className="absolute top-4 right-4 bg-black/60 text-white text-xs px-2 py-1 uppercase tracking-wider">After</span>
        </div>
      )}
    </SectionShell>
  );
};
